import { get, isEmpty, toNumber } from 'lodash';

import { UUID_REGEX_STRING } from '@/constants/regex';
import yup from '@/utils/yup';

const NUMERIC_ERROR_MESSAGE = 'Please only enter a numerical value';

export const stringSchema = yup.string().trim().nullable();
export const stringRequiredSchema = stringSchema.nonNullable().required();
export const numberSchema = yup
  .number()
  .typeError(NUMERIC_ERROR_MESSAGE)
  .transform((value: string, originalValue: string) =>
    typeof originalValue === 'string' && originalValue.trim() === '' ? undefined : value,
  )
  .nullable();
export const booleanSchema = yup.boolean().nullable();
export const percentageSchema = numberSchema.min(0).max(100);
export const percentageRequiredSchema = percentageSchema.nonNullable().required();

export const numberRequiredSchema = yup.number().typeError(NUMERIC_ERROR_MESSAGE).min(1).nonNullable().required();

export const isUUID = (uuid: unknown) =>
  typeof uuid === 'string' ? new RegExp(UUID_REGEX_STRING, 'i').test(uuid) : false;

export const isNumber = (num: unknown) =>
  ((typeof num !== 'number' && !isEmpty(num)) || typeof num === 'number') && !Number.isNaN(toNumber(num));

export const isPositiveNumber = (num: string | number | null | undefined) => isNumber(num) && toNumber(num) > 0;

export const isPositiveNumberOrZero = (num: string | number | null | undefined) => isNumber(num) && toNumber(num) >= 0;

export const isNumberGreaterThanOrEqualTo = (
  num: string | number | null | undefined,
  border: number,
  allowEmpty = false,
) => {
  const isValid = isNumber(num) && toNumber(num) >= border;
  return allowEmpty ? isValid || num === null || num === undefined : isValid;
};

export const isWithinPercentageRange = (val: string | number | null | undefined) => {
  if (!!val && isNumber(val)) {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    return num >= 0 && num <= 100;
  }

  return false;
};

export const isFormErrorsHaveError = (errors: unknown) => {
  if (!errors || typeof errors !== 'object' || Object.keys(errors).length <= 0) {
    return false;
  }

  return isFormFieldsHaveError(errors);
};

const isFormFieldsHaveError = (fields: unknown): boolean => {
  if (!fields || typeof fields !== 'object' || Object.keys(fields).length <= 0) {
    return false;
  }

  const fieldKeyMapping = Object.keys(fields).reduce(
    (result, fieldKey) => ({ ...result, [fieldKey]: get(fields, fieldKey) }),
    {} as Record<string, unknown>,
  );

  if (
    fieldKeyMapping['ref'] &&
    typeof fieldKeyMapping['type'] === 'string' &&
    typeof fieldKeyMapping['message'] === 'string' &&
    !isEmpty(fieldKeyMapping['message'])
  ) {
    return true;
  } else {
    return Object.keys(fields).some((key) => isFormFieldsHaveError(get(fields, key)));
  }
};
